import React, { useEffect, useRef, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { Skeleton } from 'primereact/skeleton';
import { exportToExcel } from 'shared/export/excel';
import { exportToCsv } from 'shared/export/csv';
import { exportToPdf } from 'shared/export/pdf';
import { exportElementToImage } from 'shared/export/image';
import { exportTimestamp } from 'shared/export/stamp';
import { alertService } from 'shared/services/alertService';

import './ReportDataTable.scss';

/**
 * Render the title icon. Supports both PrimeIcons (e.g. "pi pi-table") via an
 * <i> element and FontAwesome solid icons (e.g. "fa-solid fa-table-list" or
 * just "fa-table-list") via <FontAwesomeIcon>.
 */
const TitleIcon: React.FC<{ icon: string; className: string }> = ({
  icon,
  className,
}) => {
  if (icon.includes('fa-')) {
    // Extract the icon name (strip "fa-solid"/"fa-regular" and the "fa-" prefix).
    const name = icon
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.startsWith('fa-'))
      .map((t) => t.replace(/^fa-/, ''))
      .filter((t) => !['solid', 'regular', 'brands', 'light'].includes(t))
      .pop();
    const def = name
      ? fas[
          `fa${name
            .split('-')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join('')}` as keyof typeof fas
        ]
      : undefined;
    if (def) {
      return <FontAwesomeIcon icon={def} className={className} />;
    }
  }
  return <i className={`${className} ${icon}`} />;
};

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type ReportColumnType =
  | 'text'
  | 'number'
  | 'link'
  | 'delta'
  | 'percent'
  | 'badge'
  | 'muted'
  | 'finding'
  | 'swatch'
  | 'threshold';

export interface ReportColumn {
  /** Field key on each row. */
  key: string;
  /** Column header text. */
  label: string;
  /**
   * Rendering type:
   *  - 'number'  → right-aligned Indian format
   *  - 'link'    → drill-down first column (blue text + chevron)
   *  - 'delta'   → signed value, green/up if positive, red/down if negative
   *  - 'percent' → signed % with arrow, colored like 'delta'
   */
  type?: ReportColumnType;
  hideArrow?: boolean;

  /** Render the cell value in bold (e.g. the Students total column). */
  bold?: boolean;
  /** Whether this column is sortable. Defaults to true. */
  sortable?: boolean;
  /**
   * For a 'swatch' column: the row field holding the swatch color (hex/css
   * color). Defaults to 'color'. A small colored square is drawn before the
   * cell text.
   */
  colorKey?: string;
  /**
   * Horizontal alignment of the header + cell. Defaults: 'number' | 'delta' |
   * 'percent' align right, everything else aligns left. Set explicitly to
   * override (e.g. right-align a 'text' share-percent column).
   */
  align?: 'left' | 'right' | 'center';
  /**
   * For a 'threshold' column: color the value green when it is "good" and red
   * when it is "bad", plain otherwise. `good`/`bad` are numeric cut-offs
   * compared against the cell's number (the leading number is parsed, so "90%"
   * works). By default higher is better (value >= good → green, value < bad →
   * red). Set `invert` for metrics where LOWER is better (e.g. dropout risk):
   * value <= good → green, value > bad → red.
   */
  thresholds?: { good: number; bad: number };
  /** For a 'threshold' column: lower values are better (e.g. risk, dropout). */
  invert?: boolean;
  /**
   * Unconditionally color the cell text (e.g. a Submitted column always green,
   * a Pending column always red). Any CSS color string. Applies to 'text',
   * 'number', and 'percent'/'delta' value cells.
   */
  textColor?: string;
  /**
   * Highlight the best/worst value in this column, computed across every row
   * currently passed in `data` (not just the visible page):
   *  - 'both' → the highest value is colored green, the lowest red (e.g.
   *    Availability, where both ends are worth calling out).
   *  - 'min'  → only the lowest value is colored red (e.g. Coverage/Good,
   *    where the worst performer stands out but the best doesn't need it).
   * The cell's numeric value is parsed the same way as a 'threshold' column
   * (leading number, strips "%"/commas), so it works on 'text' columns that
   * hold formatted percentages as well as 'number' columns. No-op when every
   * row in the column shares the same value.
   */
  highlightExtremes?: 'both' | 'min';
}

export interface ReportDataTableProps<T extends Record<string, unknown>> {
  /** Title shown in the header. */
  title: string;
  /** Small hint text next to the title, e.g. "(click a row to drill down)". */
  hint?: string;
  /**
   * Optional count pill shown centered in the header (between the title and
   * the export buttons), e.g. "38 total".
   */
  countBadge?: React.ReactNode;
  /** Leading PrimeIcons class for the title. */
  icon?: string;
  /** Column definitions. */
  columns: ReportColumn[];
  /** Row data. */
  data: T[];
  /** Show the global search box. Default true. */
  searchable?: boolean;
  /** Placeholder for the search box. */
  searchPlaceholder?: string;
  /** Base file name for exports. */
  exportFileName?: string;

  suffix?: string;
  /** Show Excel/CSV/PDF export buttons. Default true. */
  exportable?: boolean;
  /** Rows per page. Default 12. */
  rows?: number;
  /** Options for the rows-per-page dropdown. */
  rowsPerPageOptions?: number[];
  /** Called when a row is clicked (drill-down). */
  onRowClick?: (row: T) => void;
  /**
   * Return true for the row that should be highlighted as active (e.g. the
   * currently-selected check). Adds the `report-dt__row--active` class.
   */
  isRowActive?: (row: T) => boolean;
  /**
   * Unique row identity field. Defaults to the first column key, but that
   * is often non-unique (e.g. repeated district names), which stops
   * PrimeReact from reconciling rows when the data array changes. Pass a
   * genuinely unique field (e.g. a school id) to fix stale rows.
   */
  dataKey?: string;

  /**
   * Distribute all columns at equal width using a fixed table layout. Use for
   * tables with uniformly short cells (e.g. period + numeric columns) so the
   * header and data align in evenly-spaced columns instead of auto-sizing to
   * content. Leave off for tables with long text columns (names/addresses).
   */
  equalColumns?: boolean;

  /**
   * Show a leading "Si.No" serial-number column (1-based, following sort and
   * pagination). Default true. Pass false for tables where a serial makes no
   * sense (e.g. a single-row detail).
   */
  showSerial?: boolean;
  /** Show the paginator. Default true; pass false to render all rows. */
  paginated?: boolean;
  /** Show a loading overlay while the table's data is being fetched. */
  loading?: boolean;
  first?: number;
  /** Called when the page changes; receives the new first-row offset. */
  onPage?: (first: number) => void;
  /**
   * Total row count for SERVER-side (lazy) pagination. When set, the table
   * renders only the current page in `data` and the paginator spans all
   * pages; combine with `first` + `onPage` to fetch each page.
   */
  totalRecords?: number;
  /** Extra class names. */
  className?: string;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const formatIndian = (value: unknown): string => {
  const num = Number(value);
  if (isNaN(num)) return String(value ?? '');
  return num.toLocaleString('en-IN');
};

// Resolve a column's alignment class. Explicit `align` wins; otherwise numeric
// types right-align and the rest left-align (default, no class needed).
// In `equalColumns` mode, columns that would otherwise right-align are centered
// instead, so short values sit in the middle of each evenly-sized column with
// balanced spacing between columns (right-align would crowd adjacent values).
const colAlignClass = (col: ReportColumn, equalColumns = false): string => {
  // Center every column's values by default (an explicit `align` on a column
  // still wins). `equalColumns` no longer changes the default — it is already
  // centered.
  void equalColumns;
  const align = col.align ?? 'center';
  if (align === 'right') return 'report-dt__col--right';
  if (align === 'center') return 'report-dt__col--center';
  return '';
};

/**
 * Column width for `equalColumns` (fixed-layout) tables.
 *
 * Not a flat 100/n share: the FIRST column is the row label (District / Block /
 * School, Type, Academic Year) and is far longer than the numeric columns
 * beside it. An even split left a drilled-down school name — e.g. "GOVT BOYS
 * HSS KOTTUR MALAYANDIPATTINAM" — with about a seventh of the table, so it
 * spilled into the next column. It now gets a double share, and the numeric
 * columns split the remainder evenly.
 */
const LABEL_COL_WEIGHT = 2;
const equalWidth = (
  col: ReportColumn,
  index: number,
  total: number
): string => {
  const units = total - 1 + LABEL_COL_WEIGHT;
  const weight = index === 0 ? LABEL_COL_WEIGHT : 1;
  return `${(weight / units) * 100}%`;
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
function ReportDataTable<T extends Record<string, unknown>>({
  title,
  suffix,
  hint,
  countBadge,
  icon = 'pi pi-table',
  columns,
  data,
  searchable = true,
  searchPlaceholder = 'Search...',
  exportFileName = 'report',
  exportable = true,
  rows = 12,
  rowsPerPageOptions = [12, 24, 48],
  onRowClick,
  isRowActive,
  dataKey,
  equalColumns = false,
  showSerial = true,
  paginated = true,
  loading = false,
  first,
  onPage,
  totalRecords,
  className = '',
}: ReportDataTableProps<T>) {
  const [globalFilter, setGlobalFilter] = useState('');

  // Captured for the PNG/JPG image export (html2canvas renders this element).
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [imageExporting, setImageExporting] = useState<'png' | 'jpg' | null>(
    null
  );

  // Sort state — we pre-sort the data ourselves so `options.rowIndex` always
  // equals the row's VISUAL (sorted) position. PrimeReact's body template
  // resolves rowIndex via value.indexOf(rowData) against the original value
  // prop; by pre-sorting we ensure indexOf returns the sorted position so
  // Si.No = rowIndex + 1 is always correct on every page and sort order.
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);

  // Sanitize the search term: spaces ARE allowed BETWEEN words (so "govt
  // higher sec" works) — only leading spaces are stripped and runs of spaces
  // are collapsed to one. A trailing space is left while typing so the user
  // can type the next word; the filter itself trims ends. This replaces the
  // earlier rule that wrongly blocked every space.
  const sanitizeSearch = (v: string) =>
    v.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');

  // Rows after applying the global search — the SAME set PrimeReact shows, so
  // exports download exactly what the user is looking at (not every row).
  // A case-insensitive substring match across every column key, mirroring
  // DataTable's globalFilterFields.
  const filteredData = useMemo(() => {
    const q = globalFilter.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) =>
      columns.some((c) =>
        String((row as Record<string, unknown>)[c.key] ?? '')
          .toLowerCase()
          .includes(q)
      )
    );
  }, [data, columns, globalFilter]);

  // Pre-sorted view — same order DataTable will render. Exports also use this
  // so downloads always match what's on screen.
  const sortedData = useMemo(() => {
    if (!sortField || !sortOrder) return filteredData;
    const dir = sortOrder;
    return [...filteredData].sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return dir * (an - bn);
      return (
        dir *
        String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
    });
  }, [filteredData, sortField, sortOrder]);

  // ── Dynamic pagination options ──────────────────────────────────────────────
  // Tiers: 12, 24. The "show all" entry equals the total row count so the
  // dropdown never offers a size that dwarfs the data.
  //   ≤ 12 rows  → no paginator (everything fits on one page)
  //   13–24 rows → [12, n]       (one page of 12 + show-all)
  //   > 24 rows  → [12, 24, n]   (standard tiers + show-all)
  // For server-side tables `totalRecords` is the authoritative total; for
  // client-side tables `data.length` (pre-filter) is used so the dropdown
  // structure stays stable while the user types in the search box.
  const dynamicRppOptions = useMemo((): number[] => {
    const total = totalRecords ?? data.length;
    if (total <= 12) return [];
    if (total <= 24) return [12, total];
    return [12, 24, total > 48 ? 48 : total];
  }, [data.length, totalRecords]);

  const showPaginator = paginated && dynamicRppOptions.length > 0;

  // Track the user's rows-per-page pick in state so it survives re-renders
  // caused by parent state changes (e.g. parent calling setFirst after a page
  // turn). Without this, passing `rows={effectiveRows}` always resets to the
  // prop default because the derived value depends only on static props.
  const [currentRows, setCurrentRows] = useState<number>(() =>
    dynamicRppOptions.includes(rows) ? rows : (dynamicRppOptions[0] ?? rows)
  );

  // When the option set changes (data arrives or total shifts), discard any
  // stale pick that's no longer in the new options and fall back to the
  // smallest available tier. `currentRows` is intentionally not in the dep
  // array — we only want this to fire when the OPTIONS change, not when the
  // user picks a new size (which would cause an infinite loop).
  const prevOptionsRef = useRef(dynamicRppOptions);
  useEffect(() => {
    if (prevOptionsRef.current === dynamicRppOptions) return;
    prevOptionsRef.current = dynamicRppOptions;
    if (
      dynamicRppOptions.length > 0 &&
      !dynamicRppOptions.includes(currentRows)
    ) {
      setCurrentRows(dynamicRppOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicRppOptions]);

  // Effective page size: no paginator → render every row; otherwise the
  // user's last picked size (persisted across re-renders in state).
  const effectiveRows = showPaginator
    ? currentRows
    : Math.max(totalRecords ?? data.length, 1);

  // Unified page-change handler. PrimeReact fires this for both page-turn
  // clicks (first changes) and rows-per-page dropdown picks (rows changes).
  //
  // PrimeReact DataTable switches to fully controlled mode (reads props.first
  // instead of its own d_first state) the moment an `onPage` handler is
  // provided.  We must therefore own `first` for ALL tables — including
  // uncontrolled ones — and pass it back.
  //
  // For UNCONTROLLED tables (no external `first` prop): we track first in
  // `internalFirst` state.
  // For CONTROLLED tables (external `first` + `onPage` props): we delegate
  // e.first back to the parent.
  const [internalFirst, setInternalFirst] = useState(0);

  // Smart page-guard: if data shrinks so that the current page no longer
  // exists (e.g. a search filter removes records), reset to page 1.
  // Only fires when data.length changes — NOT on every render — and only
  // resets when the offset is actually out of bounds so normal data-arrival
  // (length going from 0 → N) never wipes out a valid page selection.
  useEffect(() => {
    if (first != null) return; // controlled table — parent owns first
    if (internalFirst > 0 && internalFirst >= data.length) {
      setInternalFirst(0);
    }
    // `internalFirst` intentionally omitted: we only want to fire when
    // data.length changes, not on every page navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, first]);

  const handlePage = (e: { first: number; rows: number }) => {
    if (e.rows !== currentRows) setCurrentRows(e.rows);
    if (first != null) {
      onPage?.(e.first);
    } else {
      setInternalFirst(e.first);
    }
  };

  // Effective first offset: external prop for controlled tables, own state
  // for uncontrolled (always defined so PrimeReact stays in controlled mode).
  const effectiveFirst = first ?? internalFirst;

  // Sort handler — updates sort state and resets to page 1.
  const handleSort = (e: {
    sortField: string;
    sortOrder?: 1 | -1 | 0 | null;
  }) => {
    setSortField(e.sortField || null);
    setSortOrder((e.sortOrder as 1 | -1) || null);
    // Go back to page 1 so the user sees the top of the new sort order.
    if (first != null) {
      onPage?.(0);
    } else {
      setInternalFirst(0);
    }
  };

  // Placeholder rows shown as shimmer skeletons while data is fetching.
  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, i) => ({ __sk: i }) as unknown as T),
    []
  );

  const exportColumns = useMemo(
    () => columns.map((c) => ({ key: c.key, label: c.label })),
    [columns]
  );

  // For columns with `highlightExtremes`, find the min/max numeric value
  // across all rows (parsed the same way as a 'threshold' column: leading
  // number, strips "%"/commas). Skipped when every row shares one value, so
  // a flat column never gets painted red/green for no reason.
  const columnExtremes = useMemo(() => {
    const result: Record<string, { min: number; max: number }> = {};
    columns.forEach((col) => {
      if (!col.highlightExtremes) return;
      const nums = data
        .map((row) => {
          const text = String((row as Record<string, unknown>)[col.key] ?? '');
          return parseFloat(text.replace(/,/g, ''));
        })
        .filter((n) => !Number.isNaN(n));
      if (nums.length < 2) return;
      const min = Math.min(...nums);
      const max = Math.max(...nums);
      if (min === max) return;
      result[col.key] = { min, max };
    });
    return result;
  }, [columns, data]);

  // Nothing to export: no rows (empty table, or a search/filter that matched
  // none) or still loading (skeleton rows only). Disables every export
  // button (Excel/CSV/PDF/PNG/JPG) instead of letting the user download an
  // empty/placeholder file.
  const hasExportableData = !loading && sortedData.length > 0;

  // Stamp the filename with the LOCAL date + time so downloads are traceable.
  // Computed per click (not per render) so a tab left open overnight still
  // stamps the moment the user actually exported.
  const stampedFileName = () => `${exportFileName}-${exportTimestamp()}`;

  // Exports use the sorted+filtered rows so downloads match the screen.
  const handleExcel = () =>
    exportToExcel({
      data: sortedData,
      columns: exportColumns,
      fileName: stampedFileName(),
      title,
    });
  const handleCsv = () =>
    exportToCsv({
      data: sortedData,
      columns: exportColumns,
      fileName: stampedFileName(),
      title,
    });
  const handlePdf = () =>
    exportToPdf({
      data: sortedData,
      columns: exportColumns,
      fileName: stampedFileName(),
      title,
    });

  // PNG/JPG: snapshot the rendered table DOM (same footer-stamp convention as
  // AppChart's own PNG/JPG export). Captures exactly what's on screen, so a
  // search filter or sort the user applied is reflected (unlike a server-side
  // paginated table, which only ever shows the current page either way).
  const handleImageExport = async (format: 'png' | 'jpg') => {
    const el = tableWrapRef.current;
    if (!el) return;
    setImageExporting(format);
    try {
      await exportElementToImage({
        element: el,
        fileName: stampedFileName(),
        format,
        title,
      });
    } catch {
      alertService.warn('Could not export the table image.');
    } finally {
      setImageExporting(null);
    }
  };

  // Body renderer per column type.
  const cellBody = (row: T, col: ReportColumn) => {
    const raw = row[col.key];
    if (col.type === 'link') {
      // Optional leading icon: when the row carries an `icon` field, show it
      // before the link text (e.g. the caution-check icons).
      const rowIcon = (row as Record<string, unknown>).icon;
      // The chevron is a drill-down affordance, so it is only drawn when the
      // table actually handles row clicks. A `link` column on a table without
      // `onRowClick` renders as plain text — no arrow promising a drill that
      // would not happen.
      const isClickable = !!onRowClick;
      const text = String(raw ?? '');
      return (
        <span
          className={`report-dt__link${isClickable ? ' is-clickable' : ''}`}
          // Always title the cell: at school level these names are long enough
          // to be truncated, so the full value has to stay reachable on hover.
          title={isClickable ? `Drill down into ${text}` : text}
        >
          {typeof rowIcon === 'string' && rowIcon ? (
            <TitleIcon icon={rowIcon} className="report-dt__link-icon" />
          ) : null}
          {/* Own element so the ellipsis applies to the TEXT only — the icon
              and chevron stay at full size instead of being clipped away. */}
          <span className="report-dt__link-text">{text}</span>
          {isClickable && (
            <i
              className="pi pi-angle-right report-dt__chevron"
              aria-hidden="true"
            />
          )}
        </span>
      );
    }
    if (col.type === 'delta' || col.type === 'percent') {
      // No value (e.g. a baseline row / missing data) → muted dash.
      if (raw == null || raw === '') {
        return <span className="report-dt__muted">—</span>;
      }
      const num = Number(raw) || 0;
      const tone = num > 0 ? 'is-up' : num < 0 ? 'is-down' : 'is-flat';
      const arrow = col.hideArrow
        ? ''
        : num > 0
          ? 'pi pi-arrow-up'
          : num < 0
            ? 'pi pi-arrow-down'
            : '';

      const sign = num > 0 ? '+' : '';
      const value =
        col.type === 'percent'
          ? `${sign}${num}%`
          : `${sign}${formatIndian(Math.abs(num) === 0 ? 0 : num)}`;
      return (
        <span className={`report-dt__delta ${tone}`}>
          {arrow && <i className={arrow} />}
          {value}
        </span>
      );
    }
    if (col.type === 'badge') {
      const tone = String(raw ?? '')
        .toLowerCase()
        .replace(/\s+/g, '-');
      return (
        <span className={`report-dt__badge report-dt__badge--${tone}`}>
          {String(raw ?? '')}
        </span>
      );
    }
    if (col.type === 'muted') {
      return <span className="report-dt__muted">{String(raw ?? '')}</span>;
    }
    if (col.type === 'finding') {
      return <span className="report-dt__finding">{String(raw ?? '')}</span>;
    }
    if (col.type === 'threshold') {
      const text = String(raw ?? '');
      // Parse the leading number (handles "90%", "5.01%", "7,089").
      const num = parseFloat(text.replace(/,/g, ''));
      const th = col.thresholds;
      let tone = '';
      if (th && !Number.isNaN(num)) {
        if (col.invert) {
          // Lower is better (e.g. dropout risk).
          if (num <= th.good) tone = 'is-up';
          else if (num > th.bad) tone = 'is-down';
        } else {
          // Higher is better (e.g. attendance %).
          if (num >= th.good) tone = 'is-up';
          else if (num < th.bad) tone = 'is-down';
        }
      }
      return (
        <span className={`report-dt__threshold ${tone}`.trim()}>{text}</span>
      );
    }
    if (col.type === 'swatch') {
      // Small colored square (from the row's color field) + the cell text.
      const color = String(
        (row as Record<string, unknown>)[col.colorKey ?? 'color'] ?? '#cbd5e1'
      );
      return (
        <span className="report-dt__swatch-cell">
          <span
            className="report-dt__swatch"
            style={{ backgroundColor: color }}
          />
          <span className={col.bold ? 'report-dt__bold' : ''}>
            {String(raw ?? '')}
          </span>
        </span>
      );
    }
    const text = col.type === 'number' ? formatIndian(raw) : String(raw ?? '');

    // highlightExtremes wins over a static textColor — it needs the actual
    // row value to decide whether this cell is the column's min/max.
    let color = col.textColor;
    const extremes = col.highlightExtremes
      ? columnExtremes[col.key]
      : undefined;
    if (extremes) {
      const num = parseFloat(String(raw ?? '').replace(/,/g, ''));
      if (!Number.isNaN(num)) {
        if (num === extremes.min) color = '#dc2626';
        else if (col.highlightExtremes === 'both' && num === extremes.max)
          color = '#16a34a';
      }
    }

    return (
      <span
        className={col.bold ? 'report-dt__bold' : ''}
        style={color ? { color } : undefined}
      >
        {text}
      </span>
    );
  };

  return (
    <div className={`report-dt ${className}`.trim()}>
      {/* Header */}
      <div className="report-dt__head">
        <div className="report-dt__title-wrap">
          <TitleIcon icon={icon} className="report-dt__icon" />
          <h6 className="report-dt__title">{title}</h6>
          {hint && <span className="report-dt__hint">{hint}</span>}
          {suffix && <span className="app-chart__suffix"> {suffix}</span>}
          {countBadge != null && countBadge !== '' && (
            <span className="report-dt__count-badge">{countBadge}</span>
          )}
        </div>

        {exportable && (
          <div className="report-dt__exports">
            <button
              type="button"
              className="report-dt__export report-dt__export--excel"
              onClick={handleExcel}
              disabled={!hasExportableData}
              title={hasExportableData ? undefined : 'No data to export'}
            >
              <i className="pi pi-file-excel" />
              Excel
            </button>
            <button
              type="button"
              className="report-dt__export report-dt__export--csv"
              onClick={handleCsv}
              disabled={!hasExportableData}
              title={hasExportableData ? undefined : 'No data to export'}
            >
              <i className="pi pi-file" />
              CSV
            </button>
            <button
              type="button"
              className="report-dt__export report-dt__export--pdf"
              onClick={handlePdf}
              disabled={!hasExportableData}
              title={hasExportableData ? undefined : 'No data to export'}
            >
              <i className="pi pi-file-pdf" />
              PDF
            </button>
            <button
              type="button"
              className="report-dt__export report-dt__export--png"
              onClick={() => handleImageExport('png')}
              disabled={!hasExportableData || imageExporting != null}
              title={hasExportableData ? undefined : 'No data to export'}
            >
              <i
                className={
                  imageExporting === 'png'
                    ? 'pi pi-spin pi-spinner'
                    : 'pi pi-image'
                }
              />
              PNG
            </button>
            <button
              type="button"
              className="report-dt__export report-dt__export--jpg"
              onClick={() => handleImageExport('jpg')}
              disabled={!hasExportableData || imageExporting != null}
              title={hasExportableData ? undefined : 'No data to export'}
            >
              <i
                className={
                  imageExporting === 'jpg'
                    ? 'pi pi-spin pi-spinner'
                    : 'pi pi-image'
                }
              />
              JPG
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      {searchable && (
        <div className="report-dt__search">
          <span className="p-input-icon-left report-dt__search-wrap">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              // No keydown space-block: spaces between words are allowed. The
              // sanitizer only drops a LEADING space and collapses doubles;
              // leading/trailing are ignored by the filter's trim() anyway.
              onChange={(e) => setGlobalFilter(sanitizeSearch(e.target.value))}
              placeholder={searchPlaceholder}
            />
          </span>
        </div>
      )}

      {/* Table */}
      <div ref={tableWrapRef} className="report-dt__capture">
        <DataTable
          // Feed the pre-sorted rows so `options.rowIndex` = sorted position.
          // PrimeReact's rowIndex = value.indexOf(rowData); pre-sorting ensures
          // that index is the visual sequential number, not the original order.
          value={loading ? skeletonRows : sortedData}
          dataKey={dataKey ?? columns[0]?.key}
          paginator={showPaginator}
          showGridlines
          {...(totalRecords != null ? { lazy: true, totalRecords } : {})}
          first={effectiveFirst}
          onPage={handlePage}
          rows={effectiveRows}
          rowsPerPageOptions={dynamicRppOptions}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          pt={{
            paginator: {
              RPPDropdown: {
                root: { className: 'report-dt__rpp' },
                panel: { className: 'report-dt__rpp-panel' },
              },
            },
          }}
          sortField={sortField ?? undefined}
          sortOrder={(sortOrder ?? 0) as 0 | 1 | -1}
          onSort={handleSort}
          stripedRows
          responsiveLayout="scroll"
          {...(equalColumns
            ? { tableStyle: { tableLayout: 'fixed' as const, width: '100%' } }
            : {})}
          className="report-dt__table"
          onRowClick={(e) => onRowClick?.(e.data as T)}
          rowClassName={(row: T) =>
            [
              onRowClick ? 'report-dt__row--clickable' : '',
              isRowActive?.(row) ? 'report-dt__row--active' : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          emptyMessage={
            <div className="report-dt__empty">
              <i className="pi pi-inbox" />
              <span>No records found.</span>
            </div>
          }
        >
          {/* Leading Si.No serial column — 1-based.
            We DO NOT use PrimeReact's `options.rowIndex`: that index is derived
            from the configured `dataKey`, and when the dataKey is non-unique
            (e.g. a repeated "district" column used as the default key) it
            resolves to the FIRST matching row for every duplicate, which made
            the serial jump (1, 7, 13, 14…). Instead we take the row's real
            position in the pre-sorted array (`sortedData.indexOf(row)`) — the
            exact array fed as `value` — which is always its true sequential
            position regardless of dataKey. */}
          {showSerial && (
            <Column
              key="__serial"
              header="Si.No"
              headerClassName="report-dt__col--center report-dt__serial-head"
              bodyClassName="report-dt__col--center report-dt__serial-cell"
              sortable={false}
              style={{ width: '64px' }}
              body={
                loading
                  ? () => <Skeleton width="40%" height="1rem" />
                  : (row: T) => sortedData.indexOf(row) + 1
              }
            />
          )}
          {columns.map((col, colIndex) => (
            <Column
              key={col.key}
              field={col.key}
              header={col.label}
              sortable={col.sortable ?? true}
              body={
                loading
                  ? () => <Skeleton width="80%" height="1rem" />
                  : (row: T) => cellBody(row, col)
              }
              headerClassName={colAlignClass(col, equalColumns)}
              bodyClassName={colAlignClass(col, equalColumns)}
              {...(equalColumns
                ? {
                    style: {
                      width: equalWidth(col, colIndex, columns.length),
                    },
                  }
                : {})}
            />
          ))}
        </DataTable>
      </div>
    </div>
  );
}

export default ReportDataTable;
